import {
  type ExtractDocumentTypeFromTypedRxJsonSchema,
  type RxCollection,
  type RxJsonSchema,
  toTypedRxJsonSchema,
} from "rxdb";
import { useRxCollection, type useRxData } from "rxdb-hooks";
import { SYNCABLE_META } from "./common";

export const RXDB_FACILITY_COLLECTION_NAME = "facility";

export const useRxCollectionFacility = (): RxCollection<Facility> | null =>
  useRxCollection<Facility>(RXDB_FACILITY_COLLECTION_NAME);

type UseRxData = Parameters<typeof useRxData<Facility>>;
export type FacilityQueryConstructor = UseRxData["1"];

const ADDRESS = {
  type: ["object", "null"],
  properties: {
    country: {
      type: ["string", "null"],
    },
    state: {
      type: ["string", "null"],
    },
    street: {
      type: ["string", "null"],
    },
    subCounty: {
      type: ["string", "null"],
    },
    ward: {
      type: ["string", "null"],
    },
  },
} as const;

const CLINIC_SETTINGS = {
  type: "object",
  properties: {
    inventoryManagement: {
      type: "boolean",
    },
  },
  required: ["inventoryManagement"],
} as const;

const FACILITY_SCHEMA_LITERAL = {
  title: "facility schema",
  version: 1,
  description: "Represents a facility/clinic",
  type: "object",
  primaryKey: "id",
  properties: {
    id: {
      type: "string",
      maxLength: 100,
    },
    name: {
      type: "string",
    },
    addresses: {
      type: "array",
      items: ADDRESS,
    },
    organisation: {
      type: "string",
    },
    service: {
      type: ["string", "null"],
    },
    settings: CLINIC_SETTINGS,
    meta: SYNCABLE_META,
  },
  required: ["id", "name", "addresses", "organisation", "settings", "meta"],
  /*
   * It is important to set 'as const' to preserve the literal type.
   *
   * biome-ignore lint/suspicious/noExplicitAny: We infer the DB type from this
   * object, the satisfies just provides us type inference for this object.
   */
} as const satisfies RxJsonSchema<any>;

const schemaTyped = toTypedRxJsonSchema(FACILITY_SCHEMA_LITERAL);

export type Facility = ExtractDocumentTypeFromTypedRxJsonSchema<
  typeof schemaTyped
>;

export const FACILITY_SCHEMA: RxJsonSchema<Facility> = FACILITY_SCHEMA_LITERAL;
